import { useRef, useState } from "react";
import { FileText, Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { governanceError, newKey } from "../../lib/governance";
import { ReceiptPreviewDialog } from "../finance/ReceiptPreview";
import { Button } from "../ui/button";
import { Card } from "../ui/card";

const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";
const CONTEXT = "governance_meeting";

// Files of a meeting (signed minutes, a presentation, a photo of the board). Each is sent through the private files route of this module,
// then attached to the meeting — the module decides afterwards who may read it (whoever may see the meeting). Attaching stays open after the
// closing; a removed file only leaves the meeting (nothing is erased). A register document can be linked as well, from « Modifier ».
export default function MeetingFiles({ meeting, canAttach, onData }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const input = useRef(null);
  const files = meeting.files || [];

  const upload = async (picked) => {
    if (!picked.length) return;
    setBusy(true);
    try {
      let current = meeting;
      for (const file of picked) {
        const form = new FormData();
        form.append("file", file);
        const { data: sent } = await api.post("/files", form, { params: { context: CONTEXT }, headers: { "Idempotency-Key": newKey() } });
        const { data } = await api.post(`/governance/meetings/${current.id}/files`, { file_id: sent.id });
        current = data;
        onData(data);
      }
      toast.success(picked.length > 1 ? "Fichiers joints" : "Fichier joint");
    } catch (e) {
      toast.error(governanceError(e, "Fichier refusé : PDF ou image (JPEG, PNG, WebP) de 10 Mo maximum."));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };
  const remove = async (file) => {
    try {
      const { data } = await api.delete(`/governance/meetings/${meeting.id}/files/${file.id}`);
      onData(data);
    } catch (e) {
      toast.error(governanceError(e, "Impossible de retirer ce fichier."));
    }
  };

  if (!files.length && !canAttach) return null;
  return (
    <Card className="p-4 sm:p-5 space-y-3" data-testid="meeting-files">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-slate-900">Pièces jointes</h2>
        {canAttach && (
          <>
            <Button type="button" variant="outline" size="sm" onClick={() => input.current?.click()} disabled={busy} data-testid="meeting-file-add">
              {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" aria-hidden="true" /> : <Paperclip size={14} className="mr-1.5" aria-hidden="true" />}Joindre un fichier
            </Button>
            <input ref={input} type="file" className="hidden" accept={ACCEPT} multiple data-testid="meeting-file-input" onChange={(e) => upload(Array.from(e.target.files || []))} />
          </>
        )}
      </div>
      {files.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="meeting-files-empty">Aucun fichier. Vous pouvez aussi lier un document du registre depuis « Modifier ».</p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {files.map((f) => (
            <li key={f.id} className="inline-flex max-w-full items-center rounded-md border border-slate-200 bg-slate-50 text-xs text-slate-700" data-testid="meeting-file">
              <button type="button" onClick={() => setPreview({ source: { key: `file:${f.id}`, path: `/files/${f.id}` }, name: f.name })}
                className="inline-flex min-w-0 items-center gap-1.5 px-2 py-1 hover:bg-slate-100 rounded-l-md">
                <FileText size={13} aria-hidden="true" /><span className="truncate">{f.name}</span>
              </button>
              {canAttach && (
                <button type="button" aria-label={`Retirer ${f.name}`} onClick={() => remove(f)} className="px-1.5 py-1 hover:bg-slate-100 rounded-r-md" data-testid="meeting-file-remove">
                  <X size={12} aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <ReceiptPreviewDialog receipt={preview} onClose={() => setPreview(null)} description="Pièce jointe de la réunion. Chaque ouverture est journalisée." />
    </Card>
  );
}
